<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, requiredIf } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const initialState = {
  title: '',
  inboxId: null,
  templateId: null,
  scheduledAt: null,
  selectedAudience: [],
  audienceMode: 'labels',
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);

// Upload audience (mode === 'upload'): file + column->target mapping.
const audienceFile = ref(null);
const csvColumns = ref([]);
// columnMapping: { [columnName]: 'contact:phone' | 'contact:name' | 'variable:0' | 'ignore' }
const columnMapping = reactive({});

const audienceModeOptions = computed(() => [
  { value: 'labels', label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE_MODE.LABELS') },
  { value: 'manual', label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE_MODE.MANUAL') },
  { value: 'upload', label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE_MODE.UPLOAD') },
]);

const targetOptions = computed(() => {
  const opts = [
    { value: 'ignore', label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.UPLOAD.TARGET_IGNORE') },
    { value: 'contact:phone', label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.UPLOAD.TARGET_PHONE') },
    { value: 'contact:name', label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.UPLOAD.TARGET_NAME') },
  ];
  // up to 10 ad-hoc variables {{0}}..{{9}}
  for (let i = 0; i < 10; i += 1) {
    opts.push({
      value: `variable:${i}`,
      label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.UPLOAD.TARGET_VARIABLE', { index: i }),
    });
  }
  return opts;
});

const parseCsvHeader = text => {
  const firstLine = (text || '').split(/\r?\n/)[0] ?? '';
  return firstLine
    .split(',')
    .map(c => c.replace(/^"|"$/g, '').trim())
    .filter(Boolean);
};

const handleAudienceFile = async event => {
  const file = event.target.files?.[0];
  audienceFile.value = file ?? null;
  csvColumns.value = [];
  Object.keys(columnMapping).forEach(k => delete columnMapping[k]);
  if (!file) return;
  const text = await file.text();
  csvColumns.value = parseCsvHeader(text);
  csvColumns.value.forEach(col => {
    columnMapping[col] = 'ignore';
  });
};

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  scheduledAt: { required },
  selectedAudience: {
    required: requiredIf(() => state.audienceMode !== 'upload'),
  },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const hasRequiredTemplateParams = computed(() => {
  return templateParserRef.value?.v$?.$invalid === false || true;
});

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasRequiredTemplateParams.value
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, initialState);
  audienceFile.value = null;
  csvColumns.value = [];
  Object.keys(columnMapping).forEach(k => delete columnMapping[k]);
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => {
  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  // Extract template content - this should be the template message body
  const templateContent = parserData?.renderedTemplate || '';

  // Prepare template_params object with the same structure as used in contacts
  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  // variable_mapping derived from the column mapping (only ad-hoc variables).
  const variableMapping = {};
  Object.entries(columnMapping).forEach(([col, target]) => {
    if (typeof target === 'string' && target.startsWith('variable:')) {
      const index = target.split(':')[1];
      variableMapping[index] = { source: `variable:${index}`, column: col };
    }
  });

  return {
    title: state.title,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience_mode: state.audienceMode,
    variable_mapping:
      state.audienceMode === 'upload' ? variableMapping : undefined,
    audience:
      state.audienceMode === 'upload'
        ? []
        : state.selectedAudience?.map(id => ({ id, type: 'Label' })),
  };
};

// Builds the multipart payload for the audience upload (mode === 'upload').
const buildAudienceUpload = () => {
  if (state.audienceMode !== 'upload' || !audienceFile.value) return null;
  const formData = new FormData();
  formData.append('file', audienceFile.value);
  Object.entries(columnMapping).forEach(([col, target]) => {
    if (target && target !== 'ignore') {
      formData.append(`mapping[${col}]`, target);
    }
  });
  return formData;
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails(), buildAudienceUpload());
  resetState();
  handleCancel();
};

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
      </p>
    </div>

    <!-- Template Parser -->
    <WhatsAppTemplateParser
      v-if="selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
    />

    <div class="flex flex-col gap-1">
      <label
        for="audience-mode"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE_MODE.LABEL') }}
      </label>
      <ComboBox
        id="audience-mode"
        v-model="state.audienceMode"
        :options="audienceModeOptions"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE_MODE.PLACEHOLDER')
        "
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div
      v-if="state.audienceMode !== 'upload'"
      class="flex flex-col gap-1"
    >
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <div v-else class="flex flex-col gap-2">
      <label
        for="audience-file"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.UPLOAD.LABEL') }}
      </label>
      <input
        id="audience-file"
        type="file"
        accept=".csv,text/csv"
        class="block w-full text-sm text-n-slate-11 file:mr-3 file:rounded-md file:border-0 file:bg-n-alpha-2 file:px-3 file:py-1.5 file:text-n-blue-11 hover:file:bg-n-alpha-3"
        @change="handleAudienceFile"
      />
      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.UPLOAD.INFO') }}
      </p>

      <div v-if="csvColumns.length" class="flex flex-col gap-2 mt-1">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.UPLOAD.MAPPING_LABEL') }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.UPLOAD.MAPPING_INFO') }}
        </p>
        <div
          v-for="col in csvColumns"
          :key="col"
          class="grid grid-cols-2 gap-2 items-center"
        >
          <span class="text-sm truncate text-n-slate-12" :title="col">
            {{ col }}
          </span>
          <Select v-model="columnMapping[col]" :options="targetOptions" />
        </div>
      </div>
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
